// RaiseIssueButton — small contextual affordance dropped on a failing
// register row (a rejected deliverable, an overdue milestone, a red KPI
// period). Clicking navigates to the Issues tab with the
// ?raise=1&srcType=…&srcId=…&srcTitle=… deep-link; IssueTab parses those
// params and opens the create form pre-filled with the auto-link + a
// suggested title, severity and party derived from the source type.
//
// The component itself stays tiny: it is a `<Link>` styled as a secondary
// button — keyboard-navigable for free, no JS click handler. Rendered on
// the detail-expand panel of each source surface (not in the table row
// itself, since the row is the click target for expanding).

import { Link } from "react-router";
import { Flag } from "lucide-react";
import type { IssueLinkTargetType } from "../../../../types";

// Per-source aria-label helper. Screen readers hear "link" on the
// underlying anchor but the click resolves to a modal-on-arrival; the
// extra context disambiguates "raise issue" from a generic register link.
const SOURCE_TYPE_LABEL: Record<IssueLinkTargetType, string> = {
  deliverable: "this deliverable",
  milestone: "this milestone",
  kpi: "this KPI",
  "kpi-measurement": "this KPI period",
};

export interface RaiseIssueButtonProps {
  /** Source contract id. Currently unused (the deep-link navigates within
   *  the same contract page via relative `?search=…`), but kept on the
   *  prop surface so callers that don't yet know they're on the contract
   *  page can be hoisted later without a breaking change. */
  contractId?: string;
  srcType: IssueLinkTargetType;
  srcId: string;
  /** Human-readable title forwarded to the form pre-fill. The IssueTab
   *  joins it onto the suggested title prefix ("Issue raised from
   *  deliverable: {srcTitle}"). Falls back to a generic title if absent. */
  srcTitle?: string;
  /** Optional override for the button label — useful when a surface already
   *  carries severity context (e.g. "Raise issue for this breach"). */
  label?: string;
}

export default function RaiseIssueButton({
  srcType,
  srcId,
  srcTitle,
  label = "Raise issue",
}: RaiseIssueButtonProps) {
  const params = new URLSearchParams({
    tab: "issue",
    raise: "1",
    srcType,
    srcId,
  });
  if (srcTitle) params.set("srcTitle", srcTitle);
  // Same-page navigation: ManagedContractDetail reads the tab from the
  // search params and mounts IssueTab, which then handles the raise=1
  // trigger. Using `to={{ search }}` preserves the contract pathname.
  //
  // Icon-only: a Flag glyph with a "Raise issue" tooltip + accessible name.
  // The label is no longer rendered as visible text (it cluttered the failing-
  // signal detail panels) but still drives the title + aria-label so the
  // affordance stays discoverable to pointer hover and assistive tech alike.
  return (
    <Link
      to={{ search: `?${params.toString()}` }}
      data-testid="raise-issue-button"
      title={label}
      aria-label={`${label} from ${SOURCE_TYPE_LABEL[srcType]}`}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      <Flag aria-hidden="true" className="h-3.5 w-3.5" />
    </Link>
  );
}
